import { useEffect, useRef } from 'react'
import isEqual from 'lodash/isEqual'
import { usePeople } from '../helpers/people-context'

type Contact = {
  id: number
  title?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  dateOfBirth?: string | null
  phoneNumber?: string | null
}

type ContactAttributes = {
  originalContactId: number
  title?: string
  firstNames?: string
  lastName?: string
  email?: string
  dateOfBirth?: string
  phoneNumber?: string
}

type UseSyncProbateEstatePersonWithCallerProps = {
  contact: Contact | undefined
  customerIsExecutor: string | undefined
  customerIsAdministrator: string | undefined
}

const mapContactToAttributes = (contact: Contact): ContactAttributes => ({
  originalContactId: contact.id,
  title: contact.title || undefined,
  firstNames: contact.firstName || undefined,
  lastName: contact.lastName || undefined,
  email: contact.email || undefined,
  dateOfBirth: contact.dateOfBirth || undefined,
  phoneNumber: contact.phoneNumber || undefined,
})

export const useSyncProbateEstatePersonWithCaller = ({
  contact,
  customerIsExecutor,
  customerIsAdministrator,
}: UseSyncProbateEstatePersonWithCallerProps) => {
  const { syncProbateEstatePersonWithCaller } = usePeople()

  const prev = useRef<{
    contactId: number
    attributes: ContactAttributes
    isPrimaryAdministrator: boolean
    isPrimaryExecutor: boolean
  } | null>(null)

  useEffect(() => {
    if (!contact?.id) return

    const isPrimaryExecutor = customerIsExecutor === 'true'
    const isPrimaryAdministrator = customerIsAdministrator === 'true'
    const attributes = mapContactToAttributes(contact)

    const person = {
      contactId: contact.id,
      attributes,
      isPrimaryAdministrator,
      isPrimaryExecutor,
    }

    if (!isEqual(person, prev.current)) {
      syncProbateEstatePersonWithCaller({
        contactId: contact.id,
        attributes,
        isPrimaryExecutor,
        isPrimaryAdministrator,
      })
      prev.current = person
    }
  }, [
    contact,
    customerIsExecutor,
    customerIsAdministrator,
    syncProbateEstatePersonWithCaller,
  ])
}
